import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLoading } from "../../../../../hooks/useLoading";
import { LOADING_KEYS } from "../../../../../constants/loadingKeys";
import {
  getProductMasterMaterialByIdAsync,
  getProductSelectorQuestion,
  getRecommendedproductcount,
  getRecommendedProductDetail,
} from "../../../../../app/homeSlice";
import { FIRST_QUESTION, mapApiQuestion, VIEW } from "../utils";

export const useProductSelector = () => {
  const dispatch = useDispatch();
  const loading = useLoading(LOADING_KEYS.PRODUCT_SELECTOR_QUESTION);
  const cardLoading = useLoading(LOADING_KEYS.GET_RECOMMENDED_PRODUCT_COUNT);
  const detailLoading = useLoading(LOADING_KEYS.GET_RECOMMENDED_PRODUCT_DETAIL);
  const materialLoading = useLoading(LOADING_KEYS.GET_PRODUCT_MASTER_MATERIAL_BY_ID);
  const formularyId = useSelector((s) => s.user?.formularyId);

  const [steps, setSteps] = useState([{ ...FIRST_QUESTION, selected: null }]);
  const [view, setView] = useState(VIEW.ASSESSMENT);
  const [isComplete, setIsComplete] = useState(false);
  const [assessmentResults, setAssessmentResults] = useState(null);
  const [formularyData, setFormularyData] = useState(null);

  const [detailTitle, setDetailTitle] = useState(null);
  const [detailGroups, setDetailGroups] = useState([]);

  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedMaterialId, setSelectedMaterialId] = useState(null);
  const [materialDetail, setMaterialDetail] = useState(null);

  const activeIndex = isComplete ? -1 : steps.length - 1;

  const buildPath = useCallback((index, newAnswer) => [...steps.slice(0, index).map((s) => s.selected), newAnswer], [steps]);

  const selectAnswer = useCallback(
    async (stepIndex, optionLabel, apiValue) => {
      if (loading || stepIndex !== activeIndex) return;

      const answerForApi = apiValue ?? optionLabel;
      const path = buildPath(stepIndex, answerForApi);
      const res = await dispatch(getProductSelectorQuestion({ data: path }));
      if (!res?.success) return;

      if (res.data === null) {
        setSteps((prev) => {
          const updated = prev.slice(0, stepIndex + 1);
          updated[stepIndex] = { ...updated[stepIndex], selected: answerForApi };
          return updated;
        });
        setAssessmentResults(res.results ?? []);
        setIsComplete(true);
        return;
      }

      const [nextQuestion] = res.data;
      setSteps((prev) => {
        const updated = prev.slice(0, stepIndex + 1);
        updated[stepIndex] = { ...updated[stepIndex], selected: answerForApi };
        return [...updated, { ...mapApiQuestion(nextQuestion), selected: null }];
      });
    },
    [loading, activeIndex, buildPath, dispatch]
  );

  const startEdit = useCallback(
    (stepIndex) => {
      if (loading || stepIndex === activeIndex) return;
      setSteps((prev) => {
        const truncated = prev.slice(0, stepIndex + 1);
        truncated[stepIndex] = { ...truncated[stepIndex], selected: null };
        return truncated;
      });
      setIsComplete(false);
      setAssessmentResults(null);
    },
    [loading, activeIndex]
  );

  const submitAssessment = useCallback(async () => {
    if (!isComplete || !assessmentResults) return;

    setView(VIEW.RESULTS);

    const isCategoryResult = assessmentResults.some((entry) => entry !== null && typeof entry === "object");

    const payload = {
      formularyId,
      withOutCategories: !isCategoryResult ? JSON.stringify(assessmentResults) : null,
      withCategories: isCategoryResult ? JSON.stringify(assessmentResults) : null,
    };

    const res = await dispatch(getRecommendedproductcount(payload));
    if (res) {
      setFormularyData(res);
    } else {
      setFormularyData(null);
    }
  }, [isComplete, assessmentResults, formularyId, dispatch]);

  const openCategoryDetail = useCallback(
    async (category) => {
      const materialIds = (category.materialNumber || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      const payload = {
        formularyId,
        isFormulary: category.isFormulary,
        materialIds,
      };

      setDetailTitle(category.name);
      setView(VIEW.RESULT_DETAIL);

      const res = await dispatch(getRecommendedProductDetail(payload));
      if (res) {
        const groups = category.isFormulary === 1 ? res.formulary : res.nonFormulary;
        setDetailGroups(groups || []);
      }
    },
    [formularyId, dispatch]
  );

  const backToResults = useCallback(() => {
    setView(VIEW.RESULTS);
    setDetailTitle(null);
    setDetailGroups([]);
  }, []);

  const openMaterialDetail = useCallback(
    async (productCategoryId, materialId) => {
      setSelectedProductId(productCategoryId);
      setSelectedMaterialId(materialId);
      setMaterialDetail(null);
      setView(VIEW.MATERIAL_DETAIL_VIEW);

      const res = await dispatch(getProductMasterMaterialByIdAsync(materialId));
      if (res) {
        setMaterialDetail(res);
      }
    },
    [dispatch]
  );

  const handleBackToResultDetail = useCallback(() => {
    setView(VIEW.RESULT_DETAIL);
    setMaterialDetail(null);
    setSelectedProductId(null);
    setSelectedMaterialId(null);
  }, []);

  const backToAssessment = useCallback(() => {
    setView(VIEW.ASSESSMENT);
  }, []);

  const reset = useCallback(() => {
    setSteps([{ ...FIRST_QUESTION, selected: null }]);
    setView(VIEW.ASSESSMENT);
    setIsComplete(false);
    setAssessmentResults(null);
    setFormularyData(null);
    setDetailTitle(null);
    setDetailGroups([]);
    setSelectedProductId(null);
    setSelectedMaterialId(null);
    setMaterialDetail(null);
  }, []);

  return {
    steps,
    activeIndex,
    loading,
    view,
    isComplete,
    formularyData,
    cardLoading,
    detailLoading,
    detailTitle,
    detailGroups,
    materialLoading,
    materialDetail,
    selectedProductId,
    selectedMaterialId,
    selectAnswer,
    startEdit,
    submitAssessment,
    openCategoryDetail,
    backToResults,
    openMaterialDetail,
    handleBackToResultDetail,
    backToAssessment,
    reset,
  };
};
